import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, FileText, Tag, Plus, X, Download, Eye, Lock, Trash2 } from 'lucide-react';
import { colGet, colRemove, colAddTag, colRemoveTag, docsDownload, docsRead, apiErrText } from '../api';
import { useAuth } from '../context/AuthContext';

const CC=['bg-blue-100 text-blue-700','bg-green-100 text-green-700','bg-purple-100 text-purple-700','bg-orange-100 text-orange-700','bg-red-100 text-red-700','bg-yellow-100 text-yellow-700'];

export default function Collection() {
  const { isPaid } = useAuth();
  const [col,setCol]       = useState([]);
  const [loading,setLoad]  = useState(true);
  const [tagInput,setTagIn]= useState('');
  const [tagging,setTag]   = useState(null);

  useEffect(()=>{
    colGet().then(r=>setCol((r.data.collection||[]).map(d=>({
      ...d, tags:(d.tags||[]).map(t=> typeof t==='string' ? {id:null,name:t} : t)
    })))).catch(()=>{}).finally(()=>setLoad(false));
  },[]);

  const remove = async id=>{ await colRemove(id); setCol(c=>c.filter(d=>d.document_id!==id)); };

  const addTag = async colId=>{
    if(!tagInput.trim()) return;
    const r = await colAddTag(colId,tagInput.trim());
    const newTag = { id: r?.data?.id ?? null, name: tagInput.trim() };
    setCol(c=>c.map(d=>d.collection_id===colId?{...d,tags:[...(d.tags||[]),newTag]}:d));
    setTagIn(''); setTag(null);
  };

  const removeTag = async (colId,tag)=>{
    if(tag.id==null){ alert('Tag removal needs the backend update (routes/collection.js from lawoc-backend-fix2). Deploy it on Railway, then refresh this page.'); return; }
    try{
      await colRemoveTag(tag.id);
      setCol(c=>c.map(d=>d.collection_id===colId?{...d,tags:d.tags.filter(t=>t.id!==tag.id)}:d));
    }catch(e){ alert('Could not remove tag: '+await apiErrText(e)); }
  };

  const download = async doc=>{
    try{
      const r=await docsDownload(doc.document_id);
      const url=URL.createObjectURL(new Blob([r.data],{type:'application/pdf'}));
      const a=document.createElement('a'); a.href=url; a.download=doc.title+'.pdf'; a.click(); URL.revokeObjectURL(url);
    }catch(e){ alert('Download failed: '+await apiErrText(e)); }
  };

  const read = async doc=>{
    const w=window.open('','_blank'); // open synchronously so popup blockers allow it
    if(w) w.document.write('<title>Loading document...</title><p style="font-family:sans-serif;padding:24px">Loading document, please wait...</p>');
    try{
      const r=await docsRead(doc.document_id);
      const url=URL.createObjectURL(new Blob([r.data],{type:'application/pdf'}));
      if(w) w.location=url; else window.open(url,'_blank');
    }catch(e){ if(w) w.close(); alert('Read failed: '+await apiErrText(e)); }
  };

  if(loading) return <div className="text-center py-20 text-gray-400">Loading your collection...</div>;

  return(
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-blue-950">My Collection</h1>
        <span className="text-sm text-gray-400">{col.length} document{col.length!==1?'s':''} saved</span>
      </div>
      {col.length===0?(
        <div className="text-center py-20 text-gray-400">
          <Star size={48} className="mx-auto mb-3 opacity-20"/>
          <p className="font-semibold text-gray-600 mb-1">Your collection is empty</p>
          <p className="text-sm mb-4">Search for documents and save them to build your library</p>
          <Link to="/search" className="bg-blue-900 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-800 transition">Browse Documents</Link>
        </div>
      ):(
        <div className="space-y-4">
          {col.map((doc,i)=>(
            <div key={doc.collection_id} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5"><FileText size={14} className="text-blue-500"/><span className={"text-xs px-2 py-0.5 rounded-full font-medium "+CC[i%CC.length]}>{doc.category}</span></div>
                  <h3 className="font-bold text-gray-800 truncate">{doc.title}</h3>
                  <p className="text-xs text-gray-400 mb-3">{doc.language}</p>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {(doc.tags||[]).map((t,j)=>(
                      <span key={j} className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Tag size={9}/>{t.name}
                        <button onClick={()=>removeTag(doc.collection_id,t)} title="Remove tag" className="hover:text-red-500"><X size={9}/></button>
                      </span>
                    ))}
                    {tagging===doc.collection_id?(
                      <div className="flex items-center gap-1">
                        <input autoFocus value={tagInput} onChange={e=>setTagIn(e.target.value)}
                          onKeyDown={e=>{if(e.key==='Enter')addTag(doc.collection_id);if(e.key==='Escape'){setTag(null);setTagIn('');}}}
                          placeholder="Tag name..." className="text-xs border rounded-lg px-2 py-0.5 outline-none w-24 focus:border-yellow-400"/>
                        <button onClick={()=>addTag(doc.collection_id)} className="text-xs bg-yellow-500 text-blue-950 px-2 py-0.5 rounded-lg font-semibold">Add</button>
                        <button onClick={()=>{setTag(null);setTagIn('');}}><X size={12} className="text-gray-400"/></button>
                      </div>
                    ):(
                      <button onClick={()=>setTag(doc.collection_id)} className="text-xs border border-dashed border-gray-300 text-gray-400 px-2 py-0.5 rounded-full hover:border-yellow-400 hover:text-yellow-500 flex items-center gap-1">
                        <Plus size={9}/>Add Tag
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {isPaid()?(
                    <><button onClick={()=>download(doc)} className="flex items-center gap-1 text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-500"><Download size={11}/>Download</button>
                    <button onClick={()=>read(doc)} className="flex items-center gap-1 text-xs bg-blue-900 text-white px-3 py-1.5 rounded-lg hover:bg-blue-800"><Eye size={11}/>Read</button></>
                  ):(
                    <Link to="/pricing" className="flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-lg"><Lock size={11}/>Subscribe</Link>
                  )}
                  <button onClick={()=>remove(doc.document_id)} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 justify-center"><Trash2 size={11}/>Remove</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
